import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Search } from "lucide-react";
import { api, euro, data, dataOra, img, errMsg, STATO_CARD } from "@/lib/api";
import { Spinner } from "@/components/Layout";

const useList = (url) => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get(url).then((r) => setRows(r.data)), [url]);
  useEffect(() => {
    load();
  }, [load]);
  return [rows, load];
};

const act = async (fn, ok, after) => {
  try {
    await fn();
    toast.success(ok);
    after && after();
  } catch (e) {
    toast.error(errMsg(e));
  }
};

const Proposta = ({ v, reload }) => {
  const [note, setNote] = useState("");
  const p = v.proposta?.prodotto || {};
  return (
    <div className="card space-y-4" data-testid={`review-${v.id}`}>
      <div className="flex justify-between flex-wrap gap-2">
        <div><p className="caption">{v.operatore.categoria} · {v.operatore.citta}</p><h3 className="font-display text-2xl">{v.operatore.ragione_sociale}</h3></div>
        <span className="text-sm text-[#786E65]">P.IVA {v.operatore.piva} · {v.operatore.email}</span>
      </div>
      <div className="grid grid-cols-5 gap-2">
        {(v.proposta?.immagini || []).map((im) => <img key={im.slot} src={img(im.path_400)} alt="" className="aspect-[3/2] object-cover rounded-lg w-full" />)}
      </div>
      <p className="font-semibold">{p.titolo}</p>
      <div className="rich text-sm" dangerouslySetInnerHTML={{ __html: p.descrizione_html }} />
      <p className="text-sm text-[#786E65]">Tagli: {(p.tagli_cent || []).map(euro).join(", ") || "—"} {p.importo_libero && `· Libero ${euro(p.importo_min_cent)}–${euro(p.importo_max_cent)}`} · Validità {p.validita_mesi} mesi · {p.tipo_buono} · {p.frazionabile ? "frazionabile" : "non frazionabile"}</p>
      <p className="text-sm"><strong>Condizioni:</strong> {p.condizioni}</p>
      <p className="text-sm"><strong>Dove riscattare:</strong> {p.dove_riscattare}</p>
      <textarea className="field" rows={2} placeholder="Note di rifiuto (obbligatorie per rifiutare)" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`review-note-${v.id}`} />
      <div className="flex gap-3">
        <button className="btn-primary btn-sm" data-testid={`review-approva-${v.id}`} onClick={() => act(() => api.post(`/admin/vetrine/${v.id}/approva`), "Vetrina approvata", reload)}><Check size={14} />Approva</button>
        <button className="btn-outline btn-sm" disabled={note.length < 3} data-testid={`review-rifiuta-${v.id}`} onClick={() => act(() => api.post(`/admin/vetrine/${v.id}/rifiuta`, { note }), "Vetrina rifiutata", reload)}><X size={14} />Rifiuta</button>
      </div>
    </div>
  );
};

export function VetrineReview() {
  const [rows, load] = useList("/admin/vetrine?stato=in_revisione");
  if (!rows) return <Spinner />;
  if (!rows.length) return <div className="card text-center py-12 text-[#786E65]" data-testid="review-empty">Nessuna vetrina in attesa di approvazione</div>;
  return <div className="space-y-6">{rows.map((v) => <Proposta key={v.id} v={v} reload={load} />)}</div>;
}

export function OperatoriAdmin() {
  const [rows, load] = useList("/admin/operatori");
  if (!rows) return <Spinner />;
  return (
    <div className="card overflow-x-auto">
      <table className="tbl" data-testid="admin-operatori-table">
        <thead><tr><th>Attività</th><th>Città</th><th>Stato</th><th>Vetrina</th><th>Scadenza</th><th>Connect</th><th>Commissione %</th><th></th></tr></thead>
        <tbody>
          {rows.map((o) => (
            <tr key={o.id} data-testid={`admin-op-${o.id}`}>
              <td><strong>{o.ragione_sociale}</strong><br /><span className="text-xs text-[#786E65]">{o.piva} · {o.email}</span></td>
              <td>{o.citta}</td>
              <td><span className={o.sospeso ? "badge-err" : o.stato === "attivo" ? "badge-ok" : "badge-warn"}>{o.sospeso ? "sospeso" : o.stato}</span></td>
              <td>{o.stato_vetrina}</td>
              <td>{data(o.scadenza_spazio)}</td>
              <td>{o.connect_pronto ? "Sì" : "No"}</td>
              <td><input type="number" step="0.1" min={0} max={50} defaultValue={o.commissione_pct} className="field !py-1.5 w-24" data-testid={`admin-op-comm-${o.id}`}
                onBlur={(e) => Number(e.target.value) !== o.commissione_pct && act(() => api.put(`/admin/operatori/${o.id}`, { commissione_pct: Number(e.target.value) }), "Commissione aggiornata", load)} /></td>
              <td><button className="btn-secondary btn-sm" data-testid={`admin-op-sospendi-${o.id}`} onClick={() => act(() => api.put(`/admin/operatori/${o.id}`, { sospeso: !o.sospeso }), o.sospeso ? "Operatore riattivato" : "Operatore sospeso", load)}>{o.sospeso ? "Riattiva" : "Sospendi"}</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const PianoRow = ({ p, reload }) => {
  const [f, setF] = useState({ nome: p.nome, durata_mesi: p.durata_mesi, prezzo: p.prezzo_cent / 100, attivo: p.attivo });
  const save = () => act(() => api.put(`/admin/piani/${p.id}`, { nome: f.nome, durata_mesi: Number(f.durata_mesi), prezzo_cent: Math.round(Number(f.prezzo) * 100), attivo: f.attivo }), "Piano aggiornato", reload);
  return (
    <tr data-testid={`admin-piano-${p.id}`}>
      <td><input className="field !py-1.5" value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} /></td>
      <td><input type="number" className="field !py-1.5 w-20" value={f.durata_mesi} onChange={(e) => setF({ ...f, durata_mesi: e.target.value })} /></td>
      <td><input type="number" step="0.01" className="field !py-1.5 w-28" value={f.prezzo} onChange={(e) => setF({ ...f, prezzo: e.target.value })} /></td>
      <td><input type="checkbox" checked={f.attivo} onChange={(e) => setF({ ...f, attivo: e.target.checked })} /></td>
      <td><button className="btn-secondary btn-sm" onClick={save} data-testid={`admin-piano-save-${p.id}`}>Salva</button></td>
    </tr>
  );
};

export function PianiAdmin() {
  const [rows, load] = useList("/admin/piani");
  const [f, setF] = useState({ nome: "", durata_mesi: 12, prezzo: "" });
  if (!rows) return <Spinner />;
  const crea = () => act(() => api.post("/admin/piani", { nome: f.nome, durata_mesi: Number(f.durata_mesi), prezzo_cent: Math.round(Number(f.prezzo) * 100), attivo: true }), "Piano creato", () => { setF({ nome: "", durata_mesi: 12, prezzo: "" }); load(); });
  return (
    <div className="space-y-6">
      <div className="card overflow-x-auto">
        <table className="tbl"><thead><tr><th>Nome</th><th>Mesi</th><th>Prezzo €</th><th>Attivo</th><th></th></tr></thead>
          <tbody>{rows.map((p) => <PianoRow key={p.id} p={p} reload={load} />)}</tbody></table>
      </div>
      <div className="card grid sm:grid-cols-4 gap-3 items-end">
        <input className="field" placeholder="Nome piano" value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} data-testid="nuovo-piano-nome" />
        <input type="number" className="field" placeholder="Durata mesi" value={f.durata_mesi} onChange={(e) => setF({ ...f, durata_mesi: e.target.value })} data-testid="nuovo-piano-mesi" />
        <input type="number" step="0.01" className="field" placeholder="Prezzo €" value={f.prezzo} onChange={(e) => setF({ ...f, prezzo: e.target.value })} data-testid="nuovo-piano-prezzo" />
        <button className="btn-primary" onClick={crea} disabled={!f.nome || !f.prezzo} data-testid="nuovo-piano-btn">Crea piano</button>
      </div>
    </div>
  );
}

export function PagamentiAdmin() {
  const [rows] = useList("/admin/pagamenti");
  if (!rows) return <Spinner />;
  return (
    <div className="card overflow-x-auto">
      <table className="tbl" data-testid="admin-pagamenti-table">
        <thead><tr><th>Data</th><th>Operatore</th><th>Piano</th><th>Importo</th><th>Stato</th></tr></thead>
        <tbody>{rows.map((p) => <tr key={p.id}><td>{dataOra(p.created_at)}</td><td>{p.operatore}</td><td>{p.piano_nome}</td><td>{euro(p.importo_cent)}</td><td><span className={p.stato === "pagato" ? "badge-ok" : "badge-muted"}>{p.stato}</span></td></tr>)}</tbody>
      </table>
    </div>
  );
}

export function GiftCardAdmin() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const cerca = useCallback(() => api.get("/admin/giftcard", { params: { q } }).then((r) => setRows(r.data)), [q]);
  useEffect(() => {
    cerca();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-6">
      <form className="flex gap-2 max-w-xl" onSubmit={(e) => { e.preventDefault(); cerca(); }}>
        <input className="field" placeholder="Ultime 4 cifre, email o attività" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-gc-search-input" />
        <button className="btn-primary" data-testid="admin-gc-search-btn"><Search size={16} /></button>
      </form>
      {!rows ? <Spinner /> : (
        <div className="card overflow-x-auto">
          <table className="tbl" data-testid="admin-gc-table">
            <thead><tr><th>Codice</th><th>Attività</th><th>Importo</th><th>Saldo</th><th>Stato</th><th>Scadenza</th><th>Acquirente</th><th></th></tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} data-testid={`admin-gc-${c.id}`}>
                  <td className="font-code">…{c.ultime4 || "----"}</td><td>{c.operatore}</td><td>{euro(c.importo_cent)}</td><td>{euro(c.saldo_cent)}</td>
                  <td><span className={STATO_CARD[c.stato]}>{c.stato}</span></td><td>{data(c.scadenza)}</td><td className="text-xs">{c.acquirente_email}</td>
                  <td>{c.stato === "attiva" && c.mai_usata && (
                    <button className="btn-outline btn-sm" data-testid={`admin-gc-rimborsa-${c.id}`}
                      onClick={() => window.confirm("Rimborsare e annullare questa gift card?") && act(() => api.post(`/admin/giftcard/${c.id}/rimborso`), "Rimborso eseguito, carta annullata", cerca)}>Rimborsa</button>
                  )}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={8} className="text-center text-[#786E65]">Nessun risultato</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function AuditAdmin() {
  const [rows] = useList("/admin/audit");
  if (!rows) return <Spinner />;
  return (
    <div className="card overflow-x-auto">
      <table className="tbl" data-testid="admin-audit-table">
        <thead><tr><th>Data</th><th>Utente</th><th>Azione</th><th>Dettagli</th></tr></thead>
        <tbody>{rows.map((a) => <tr key={a.id}><td>{dataOra(a.created_at)}</td><td>{a.attore_email}</td><td>{a.azione}</td><td className="font-code text-xs break-all">{JSON.stringify(a.dettagli)}</td></tr>)}</tbody>
      </table>
    </div>
  );
}
